import { Box, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import type { AnswerValue, Question } from '@/benefits-flow/screener/questions';
import type { Subject } from '@/benefits-flow/engine/types';

// Renders one quiz question as big, tappable option cards. Single-choice and
// yes/no auto-advance (the parent advances on change); multi-select toggles and
// waits for a Continue. Also surfaces the answer's subject — About Mum / the
// carer / the partner / the household — the modelling the prototype shows off.
export type QuestionFieldProps = {
  question: Question;
  recipientName: string;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue) => void;
};

const subjectLabel = (subject: Subject, name: string): string =>
  ({
    'care-recipient': `About ${name}`,
    carer: 'About the carer',
    partner: 'About the partner',
    household: 'About the household',
  })[subject];

// One tappable option card.
const OptionCard = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <Stack
    component='button'
    type='button'
    onClick={onClick}
    direction='row'
    spacing={1}
    sx={{
      alignItems: 'center',
      justifyContent: 'space-between',
      px: 2,
      py: 1.75,
      width: '100%',
      borderRadius: `${radius.md}px`,
      border: 1,
      borderColor: selected ? 'custom.coral.600' : 'custom.neutral.200',
      bgcolor: selected ? 'custom.coral.50' : 'background.paper',
      textAlign: 'left',
      cursor: 'pointer',
    }}
  >
    <Typography
      variant='subtitle2'
      color={selected ? 'custom.coral.700' : 'text.primary'}
    >
      {label}
    </Typography>
    {selected && (
      <Box sx={{ color: 'custom.coral.700', display: 'flex' }}>
        <Icon icon={'check' as never} fontSize={16} />
      </Box>
    )}
  </Stack>
);

const QuestionField = ({
  question,
  recipientName,
  value,
  onChange,
}: QuestionFieldProps) => {
  const q = question;
  const prompt = (text: string) => text.replaceAll('{name}', recipientName);

  const renderOptions = () => {
    if (q.type === 'boolean') {
      return (
        <Stack spacing={1}>
          <OptionCard
            label='Yes'
            selected={value === true}
            onClick={() => onChange(true)}
          />
          <OptionCard
            label='No'
            selected={value === false}
            onClick={() => onChange(false)}
          />
        </Stack>
      );
    }

    if (q.type === 'multi-select') {
      const selected = (value as string[]) ?? [];
      const noneId = q.options?.find((o) => o.value === 'none')?.id;
      const toggle = (id: string) => {
        if (id === noneId) {
          onChange([id]);
          return;
        }
        const next = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected.filter((s) => s !== noneId), id];
        onChange(next);
      };
      return (
        <Stack spacing={1}>
          {q.options?.map((o) => (
            <OptionCard
              key={o.id}
              label={o.label}
              selected={selected.includes(o.id)}
              onClick={() => toggle(o.id)}
            />
          ))}
        </Stack>
      );
    }

    // choice
    return (
      <Stack spacing={1}>
        {q.options?.map((o) => (
          <OptionCard
            key={o.id}
            label={o.label}
            selected={value === o.id}
            onClick={() => onChange(o.id)}
          />
        ))}
      </Stack>
    );
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant='overline' color='text.secondary'>
        {subjectLabel(q.subject, recipientName)}
      </Typography>
      <Typography variant='h3'>{prompt(q.prompt)}</Typography>
      {q.help && (
        <Typography variant='body2' color='text.secondary'>
          {prompt(q.help)}
        </Typography>
      )}
      <Box sx={{ pt: 0.5 }}>{renderOptions()}</Box>
    </Stack>
  );
};

QuestionField.displayName = 'QuestionField';
export default QuestionField;
